// reportingApi.js

/*************************************************************
 * reportingApi.js - Enhanced API Client for Reporting System
 * Now includes model visualization support, classification confirmation, heatmap aggregation,
 * and analytics configuration management
 * 
 * ============================================================
 * AUTHENTICATION FIX (2024):
 * ============================================================
 * All authentication now uses the global authenticatedFetch from authUtils
 * which is automatically set up by authUtils.js (Electron) or authUtils.web.js (web).
 * 
 * Benefits of this approach:
 * ✅ Automatic token refresh on 401 errors
 * ✅ Camera service integration on token updates
 * ✅ Consistent auth handling across Electron and web deployments  
 * ✅ Single source of truth for authentication logic
 * ✅ No hardcoded token access anywhere in this file
 * 
 * How it works:
 * - Both authUtils files replace window.fetch with authenticatedFetch
 * - All fetch() calls in this file automatically use authenticatedFetch
 * - No need to manually add Authorization headers or handle token refresh
 *************************************************************/

// const API_CONFIG = {
//     BASE_URL: 'http://localhost:1019/api/v1',
//     TIMEOUT: 30000,
//     RETRY_ATTEMPTS: 3
// };

const API_CONFIG = {
    // Environment-aware API URL
    BASE_URL: import.meta.env.VITE_API_URL || 
              (window.location.hostname === 'localhost' 
                ? 'http://localhost:1019/api/v1' 
                : 'https://api.synthomaticai.com/api/v1'),
                // : 'https://eyeq-cloud-api.onrender.com/api/v1'),
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 3
};

export class APIError extends Error {
    constructor(message, status, endpoint) {
        super(message);
        this.name = 'APIError';
        this.status = status;
        this.endpoint = endpoint;
    }
}

export class ReportingAPI {
    constructor(baseUrl = API_CONFIG.BASE_URL) {
        this.baseUrl = baseUrl;
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
        this.requestCount = 0;
    }


    // ==================== MODEL/PLUGIN MANAGEMENT ====================
    
    /**
     * Fetch available model plugins from backend
     * This replaces the inferenceService.fetchAvailableModels method
     * @param {string} projectName - Optional project name to filter plugins
     * @returns {Promise<Array>} - Array of available model plugins
     */
    async getAvailableModels(projectName = null) {
        try {
            let url = `${this.baseUrl}/inference/plugins`;
            if (projectName) {
                url += `?project_name=${encodeURIComponent(projectName)}`;
            }

            console.log('[API] Fetching available models from:', url);
            
            const response = await this.request(url, {
                method: 'GET',
                bypassCache: true // Always get fresh model data
            });

            // Handle StandardResponse format from FastAPI
            if (response && response.success !== false) {
                const models = response.data || response || [];
                console.log('[API] Available models fetched:', models);
                return models;
            } else {
                throw new Error(response.error || 'Failed to fetch available models');
            }
        } catch (error) {
            console.error('[API] Error fetching available models:', error);
            // Return empty array on error to prevent breaking the UI
            return [];
        }
    }



    async getAlerts(options = {}) {
        console.log('[API] Getting user alerts');
        try {
            const response = await this.request(`${this.baseUrl}/general/alerts`, { 
                method: 'GET', 
                bypassCache: true,
                ...options 
            });
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting alerts:', error);
            // Return empty array if alerts fail to load
            return {
                success: true,
                data: []
            };
        }
    }

    async getAlertsSummary(options = {}) {
        console.log('[API] Getting alerts summary');
        try {
            const response = await this.request(`${this.baseUrl}/general/alerts/summary`, { 
                method: 'GET', 
                bypassCache: true,
                ...options 
            });
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting alerts summary:', error);
            return {
                success: true,
                data: {
                    total_alerts: 0,
                    active_alerts: 0,
                    acknowledged_alerts: 0,
                    severity_breakdown: {},
                    has_critical_alerts: false
                }
            };
        }
    }

    async acknowledgeAlert(alertId) {
        console.log('[API] Acknowledging alert:', alertId);
        const url = `${this.baseUrl}/general/alerts/acknowledge`;
        const response = await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ alert_id: alertId })
        });
        return {
            success: true,
            data: response
        };
    }

    async resolveAlert(alertId) {
        console.log('[API] Resolving alert:', alertId);
        const url = `${this.baseUrl}/general/alerts/resolve`;
        const response = await this.request(url, {
            method: 'POST', 
            body: JSON.stringify({ alert_id: alertId })
        });
        return {
            success: true,
            data: response
        };
    }

    // ==================== NEW: IN-APP NOTIFICATIONS METHODS ====================
    
    async getInAppNotifications(limit = 50, options = {}) {
        console.log('[API] Getting in-app notifications');
        try {
            const url = `${this.baseUrl}/general/notifications?limit=${limit}`;
            const response = await this.request(url, { 
                method: 'GET', 
                bypassCache: true,
                ...options 
            });
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting notifications:', error);
            return {
                success: true,
                data: []
            };
        }
    }

    async markNotificationAsRead(notificationId) {
        console.log('[API] Marking notification as read:', notificationId);
        const url = `${this.baseUrl}/general/notifications/read`;
        const response = await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ notification_id: notificationId })
        });
        return {
            success: true,
            data: response
        };
    }

    // ==================== ENHANCED ALERT UTILITIES ====================
    
    /**
     * Get alerts with automatic refresh capability
     * @param {boolean} forceRefresh - Force bypass cache
     * @returns {Promise<Object>} - Alerts response
     */
    async getAlertsWithRefresh(forceRefresh = false) {
        const options = forceRefresh ? { bypassCache: true } : {};
        return await this.getAlerts(options);
    }

    /**
     * Check if user has any active alerts
     * @returns {Promise<boolean>} - Whether user has active alerts
     */
    async hasActiveAlerts() {
        try {
            const summary = await this.getAlertsSummary();
            return summary.data.active_alerts > 0;
        } catch (error) {
            console.error('[API] Error checking active alerts:', error);
            return false;
        }
    }

    /**
     * Get count of unread notifications
     * @returns {Promise<number>} - Count of unread notifications
     */
    async getUnreadNotificationCount() {
        try {
            const notifications = await this.getInAppNotifications(100);
            const unreadCount = notifications.data.filter(n => !n.read).length;
            console.log(`[API] Found ${unreadCount} unread notifications`);
            return unreadCount;
        } catch (error) {
            console.error('[API] Error getting unread notification count:', error);
            return 0;
        }
    }

    /**
     * Test notification settings by triggering a test alert
     * @returns {Promise<Object>} - Test result
     */
    async testNotificationSettings() {
        console.log('[API] Testing notification settings');
        try {
            const url = `${this.baseUrl}/analytics/config/notifications/test`;
            const response = await this.request(url, {
                method: 'POST'
            });
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error testing notification settings:', error);
            throw error;
        }
    }

    /**
     * Format alert for display
     * @param {Object} alert - Alert object
     * @returns {Object} - Formatted alert
     */
    formatAlertForDisplay(alert) {
        if (!alert) return null;

        const formatters = {
            defect_rate: (threshold, triggerValue) => 
                `Defect rate ${triggerValue}% exceeded threshold of ${threshold}%`,
            low_confidence: (threshold, triggerValue) => 
                `Model confidence ${triggerValue}% below threshold of ${threshold}%`,
            station_offline: (threshold, triggerValue) => 
                `Station ${triggerValue} is offline`,
            session_duration: (threshold, triggerValue) => 
                `Session duration ${triggerValue} minutes exceeded threshold of ${threshold} minutes`
        };

        const formatter = formatters[alert.rule_type];
        const message = formatter 
            ? formatter(alert.threshold, alert.trigger_value || alert.triggerValue)
            : alert.rule_name;

        return {
            ...alert,
            formatted_message: message,
            time_ago: this.formatTimeAgo(alert.triggered_at),
            severity_class: this.getAlertSeverityClass(alert.severity),
            status_class: this.getAlertStatusClass(alert.status)
        };
    }

    /**
     * Get CSS class for alert severity
     * @param {string} severity - Alert severity
     * @returns {string} - CSS class
     */
    getAlertSeverityClass(severity) {
        const classMap = {
            'low': 'text-info',
            'medium': 'text-warning',
            'high': 'text-danger',
            'critical': 'text-dark bg-danger text-white'
        };
        return classMap[severity] || 'text-secondary';
    }

    /**
     * Get CSS class for alert status
     * @param {string} status - Alert status
     * @returns {string} - CSS class
     */
    getAlertStatusClass(status) {
        const classMap = {
            'active': 'badge-danger',
            'acknowledged': 'badge-warning',
            'resolved': 'badge-success'
        };
        return classMap[status] || 'badge-secondary';
    }

    /**
     * Format timestamp as time ago
     * @param {string} timestamp - ISO timestamp
     * @returns {string} - Formatted time ago
     */
    formatTimeAgo(timestamp) {
        if (!timestamp) return 'Unknown';
        
        const now = new Date();
        const alertTime = new Date(timestamp);
        const diffMs = now - alertTime;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return alertTime.toLocaleDateString();
    }

    // ==================== ALERT POLLING UTILITIES ====================
    
    /**
     * Start polling for alerts
     * @param {Function} callback - Callback function for new alerts
     * @param {number} interval - Polling interval in milliseconds
     * @returns {number} - Interval ID for clearing
     */
    startAlertPolling(callback, interval = 30000) {
        console.log(`[API] Starting alert polling every ${interval}ms`);
        
        const pollAlerts = async () => {
            try {
                const alerts = await this.getAlertsWithRefresh(true);
                const notifications = await this.getInAppNotifications(50);
                
                if (callback && typeof callback === 'function') {
                    callback({
                        alerts: alerts.data,
                        notifications: notifications.data,
                        timestamp: new Date().toISOString()
                    });
                }
            } catch (error) {
                console.error('[API] Error during alert polling:', error);
            }
        };

        // Poll immediately, then on interval
        pollAlerts();
        return setInterval(pollAlerts, interval);
    }

    /**
     * Stop alert polling
     * @param {number} intervalId - Interval ID to clear
     */
    stopAlertPolling(intervalId) {
        if (intervalId) {
            clearInterval(intervalId);
            console.log('[API] Stopped alert polling');
        }
    }

    // ==================== BULK ALERT OPERATIONS ====================
    
    /**
     * Acknowledge multiple alerts
     * @param {Array<string>} alertIds - Array of alert IDs
     * @returns {Promise<Object>} - Results
     */
    async acknowledgeMultipleAlerts(alertIds) {
        console.log('[API] Acknowledging multiple alerts:', alertIds);
        
        const results = await Promise.allSettled(
            alertIds.map(id => this.acknowledgeAlert(id))
        );

        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;

        return {
            success: failed === 0,
            successful_count: successful,
            failed_count: failed,
            total_count: alertIds.length
        };
    }

    /**
     * Resolve multiple alerts
     * @param {Array<string>} alertIds - Array of alert IDs
     * @returns {Promise<Object>} - Results
     */
    async resolveMultipleAlerts(alertIds) {
        console.log('[API] Resolving multiple alerts:', alertIds);
        
        const results = await Promise.allSettled(
            alertIds.map(id => this.resolveAlert(id))
        );

        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;

        return {
            success: failed === 0,
            successful_count: successful,
            failed_count: failed,
            total_count: alertIds.length
        };
    }

    // ==================== INTEGRATION WITH EXISTING STATS ====================
    
    /**
     * Get enhanced stats that include alert information
     * @returns {Object} - Enhanced stats
     */
    getEnhancedStats() {
        const baseStats = this.getStats();
        
        return {
            ...baseStats,
            alert_features: {
                alerts_enabled: true,
                notifications_enabled: true,
                polling_capable: true,
                bulk_operations: true
            }
        };
    }

// NEW: Alert check settings methods
    async getAlertCheckSettings(options = {}) {
        try {
            console.log('[API] Getting alert check settings...');
            const url = `${this.baseUrl}/analytics/config/alert-check-settings`;
            const response = await this.request(url, { method: 'GET', bypassCache: true, ...options });
            console.log('[API] Alert check settings response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting alert check settings:', error);
            // Return default alert check settings if API fails
            return {
                success: true,
                data: {
                    inspection_check_enabled: true,
                    inspection_interval: 3,
                    time_check_enabled: true,
                    time_interval: 5
                }
            };
        }
    }

    async saveAlertCheckSettings(settings) {
        console.log('[API] Saving alert check settings:', settings);
        try {
            const url = `${this.baseUrl}/analytics/config/alert-check-settings`;
            const response = await this.request(url, {
                method: 'POST',
                body: JSON.stringify(settings)
            });
            console.log('[API] Save alert check settings response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error saving alert check settings:', error);
            throw error;
        }
    }


    async request(url, options = {}) {
        const cacheKey = `${url}_${JSON.stringify(options)}`;
        this.requestCount++;
    
        // Check cache only for GET requests and if bypassCache is not set
        if (options.method === 'GET' && !options.bypassCache && this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey);
            if (Date.now() - cached.timestamp < this.cacheTimeout) {
                console.log(`[API] Cache hit for ${url} (request #${this.requestCount})`);
                return cached.data;
            } else {
                // Remove expired cache entry
                this.cache.delete(cacheKey);
            }
        }
    
        // FIXED: Don't manually add Authorization headers - authenticatedFetch handles this
        // Just add Content-Type and custom headers
        const headers = {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...options.headers
        };
    
        const config = {
            ...options,
            headers: headers,
            // credentials: 'include' is handled by authenticatedFetch
        };
        
        // Add cache-busting parameter if bypassCache is true
        const finalUrl = options.bypassCache ? `${url}${url.includes('?') ? '&' : '?'}t=${new Date().getTime()}` : url;

        try {
            console.log(`[API] Requesting ${finalUrl}`, config);
            // FIXED: window.fetch is replaced by authenticatedFetch from authUtils
            // authenticatedFetch automatically handles 401s with token refresh and redirect
            const response = await fetch(finalUrl, config);
    
            // Only handle other errors here (401 is handled by authenticatedFetch)
            if (!response.ok) {
                if (response.status === 404) {
                    console.warn(`[API] Endpoint not found: ${url}`);
                    throw new APIError(`Endpoint not found: ${url}`, 404, url);
                }
                const errorText = await response.text();
                throw new APIError(errorText || `HTTP ${response.status}: ${response.statusText}`, response.status, url);
            }
    
            const responseJson = await response.json();
            console.log(`[API] Response from ${url}:`, responseJson);
    
            if (options.method === 'GET' && !options.bypassCache) {
                // Note: We cache the extracted data property, not the whole response object
                this.cache.set(cacheKey, {
                    data: responseJson.data, // Store data property from the response
                    timestamp: Date.now()
                });
            }
            
            // Return the data property from the JSON response
            return responseJson.data || responseJson; 
        } catch (error) {
            console.error(`[API] Error for ${url}:`, error);
            if (error instanceof APIError) throw error;
            throw new APIError(`Network error: ${error.message}`, 0, url);
        }
    }

    // ==================== AUTH ENDPOINTS ====================
    
    async checkSession(options = {}) {
        const url = `${this.baseUrl}/auth/me`;
        return this.request(url, { method: 'GET', ...options });
    }
    
    // ==================== DASHBOARD ENDPOINTS ====================
    
    async getDashboardData(filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        if (!params.has('dateRange')) {
            params.set('dateRange', 'week');
        }
        const url = `${this.baseUrl}/reports/dashboard?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getDashboardTrend(filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        params.set('level', 'dashboard');
        params.set('identifier', 'user');
        if (!params.has('dateRange')) {
            params.set('dateRange', 'week');
        }
        const url = `${this.baseUrl}/reports/trend?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    // ==================== ANALYTICS CONFIGURATION ENDPOINTS ====================

    // Preferences methods
    async getPreferences(options = {}) {
        try {
            console.log('[API] Getting user preferences...');
            const url = `${this.baseUrl}/analytics/config/preferences`;
            const response = await this.request(url, { method: 'GET', bypassCache: true, ...options });
            console.log('[API] Preferences response:', response);
            
            // Return the data directly since our request method already extracts .data
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting preferences:', error);
            // Return default preferences if API fails
            return {
                success: true,
                data: {
                    theme: 'light',
                    itemsPerPage: 20,
                    default_date_range: 'week',
                    autoRefresh: false,
                    refreshInterval: 30,
                    show_confidence_scores: true,
                    email_notifications: true,
                    includeManualImages: true,
                    workingHoursStart: '08:00',
                    workingHoursEnd: '18:00',
                    workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday']
                }
            };
        }
    }

    async savePreferences(preferences) {
        console.log('[API] Saving user preferences:', preferences);
        try {
            const url = `${this.baseUrl}/analytics/config/preferences`;
            const response = await this.request(url, {
                method: 'POST',
                body: JSON.stringify(preferences)
            });
            console.log('[API] Save preferences response:', response);
            
            // Clear cache to ensure fresh data on next load
            this.clearCache();
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error saving preferences:', error);
            throw error;
        }
    }

    // Notification settings methods
    async getNotificationSettings(options = {}) {
        try {
            console.log('[API] Getting notification settings...');
            const url = `${this.baseUrl}/analytics/config/notifications`;
            const response = await this.request(url, { method: 'GET', bypassCache: true, ...options });
            console.log('[API] Notification settings response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting notification settings:', error);
            // Return default notification settings if API fails
            return {
                success: true,
                data: {
                    email_enabled: false,
                    sms_enabled: false,
                    app_enabled: true,
                    light_tower_enabled: false,
                    email: '',
                    phone: '',
                    defect_notifications: true,
                    station_offline_notifications: true,
                    session_completed_notifications: false,
                    daily_summary_enabled: false
                }
            };
        }
    }

    async saveNotificationSettings(settings) {
        console.log('[API] Saving notification settings:', settings);
        try {
            const url = `${this.baseUrl}/analytics/config/notifications`;
            const response = await this.request(url, {
                method: 'POST',
                body: JSON.stringify(settings)
            });
            console.log('[API] Save notification settings response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error saving notification settings:', error);
            throw error;
        }
    }

    // Alert rules methods
    async getAlertRules(options = {}) {
        try {
            console.log('[API] Getting alert rules...');
            const url = `${this.baseUrl}/analytics/config/alert-rules`;
            const response = await this.request(url, { method: 'GET', bypassCache: true, ...options });
            console.log('[API] Alert rules response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error getting alert rules:', error);
            // Return default alert rules if API fails
            return {
                success: true,
                data: [
                    {
                        id: 1,
                        name: 'High Defect Rate Alert',
                        type: 'defect_rate',
                        threshold: 10,
                        enabled: true,
                        conditions: { timeframe: '1h', min_samples: 10 }
                    },
                    {
                        id: 2,
                        name: 'Station Offline Alert',
                        type: 'station_offline',
                        threshold: 5,
                        enabled: true,
                        conditions: { timeframe: '5m' }
                    },
                    {
                        id: 3,
                        name: 'Low Model Confidence',
                        type: 'low_confidence',
                        threshold: 70,
                        enabled: true,
                        conditions: { timeframe: '30m', min_samples: 5 }
                    }
                ]
            };
        }
    }

    async saveAlertRules(rules) {
        console.log('[API] Saving alert rules:', rules);
        try {
            const url = `${this.baseUrl}/analytics/config/alert-rules`;
            const response = await this.request(url, {
                method: 'POST',
                body: JSON.stringify({ rules })
            });
            console.log('[API] Save alert rules response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error saving alert rules:', error);
            throw error;
        }
    }

    async deleteAlertRule(ruleId) {
        console.log('[API] Deleting alert rule:', ruleId);
        try {
            const url = `${this.baseUrl}/analytics/config/alert-rules/${ruleId}`;
            const response = await this.request(url, { method: 'DELETE' });
            console.log('[API] Delete alert rule response:', response);
            
            return {
                success: true,
                data: response
            };
        } catch (error) {
            console.error('[API] Error deleting alert rule:', error);
            throw error;
        }
    }

    // Configuration management methods (admin)
    async getSystemConfigStatus() {
        console.log('[API] Getting system configuration status...');
        const url = `${this.baseUrl}/analytics/config/system/status`;
        const response = await this.request(url, { method: 'GET' });
        return {
            success: true,
            data: response
        };
    }

    async resetUserToDefaults(targetUser) {
        console.log('[API] Resetting user to defaults:', targetUser);
        const url = `${this.baseUrl}/analytics/config/system/reset-defaults`;
        const response = await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ target_user: targetUser })
        });
        return {
            success: true,
            data: response
        };
    }

    // Configuration import/export
    async exportConfiguration() {
        console.log('[API] Exporting user configuration...');
        const url = `${this.baseUrl}/analytics/config/export`;
        const response = await this.request(url, { method: 'GET' });
        return {
            success: true,
            data: response
        };
    }

    async importConfiguration(configData) {
        console.log('[API] Importing user configuration:', configData);
        const url = `${this.baseUrl}/analytics/config/import`;
        const response = await this.request(url, {
            method: 'POST',
            body: JSON.stringify(configData)
        });
        return {
            success: true,
            data: response
        };
    }

    // ==================== THEME AND UI HELPER METHODS ====================

    // Apply theme to the interface
    applyTheme(themeName) {
        console.log('[API] Applying theme:', themeName);
        
        // Remove existing theme classes
        document.body.classList.remove('theme-light', 'theme-dark', 'theme-system');
        
        // Add new theme class
        document.body.classList.add(`theme-${themeName}`);
        
        // If system theme, detect preference
        if (themeName === 'system') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            document.body.classList.add(prefersDark ? 'theme-dark' : 'theme-light');
        }
        
        // Store theme preference
        localStorage.setItem('preferred-theme', themeName);
        
        // Emit theme change event for components that need to update
        window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: themeName } }));
    }

    // Get current theme
    getCurrentTheme() {
        return localStorage.getItem('preferred-theme') || 'light';
    }

    // ==================== CONFIGURATION VALIDATION METHODS ====================

    // Validate configuration data
    validatePreferences(preferences) {
        const errors = [];
        
        // Validate theme
        if (!['light', 'dark', 'system'].includes(preferences.theme)) {
            errors.push('Invalid theme selection');
        }
        
        // Validate items per page
        if (preferences.itemsPerPage < 5 || preferences.itemsPerPage > 200) {
            errors.push('Items per page must be between 5 and 200');
        }
        
        // Validate refresh interval
        if (preferences.autoRefresh && (preferences.refreshInterval < 10 || preferences.refreshInterval > 300)) {
            errors.push('Refresh interval must be between 10 and 300 seconds');
        }
        
        // Validate working hours
        if (preferences.workingHoursStart >= preferences.workingHoursEnd) {
            errors.push('Working hours start time must be before end time');
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }

    validateNotificationSettings(settings) {
        const errors = [];
        
        // Validate email if email notifications are enabled
        if (settings.email_enabled) {
            if (!settings.email) {
                errors.push('Email address is required when email notifications are enabled');
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.email)) {
                errors.push('Invalid email address format');
            }
        }
        
        // Validate phone if SMS notifications are enabled
        if (settings.sms_enabled) {
            if (!settings.phone) {
                errors.push('Phone number is required when SMS notifications are enabled');
            } else if (!/^\+?[\d\s\-\(\)]+$/.test(settings.phone)) {
                errors.push('Invalid phone number format');
            }
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }

    validateAlertRule(rule) {
        const errors = [];
        
        // Validate required fields
        if (!rule.name || rule.name.trim().length === 0) {
            errors.push('Alert rule name is required');
        }
        
        if (!['defect_rate', 'station_offline', 'low_confidence', 'session_duration'].includes(rule.type)) {
            errors.push('Invalid alert rule type');
        }
        
        // Validate threshold
        if (typeof rule.threshold !== 'number' || rule.threshold <= 0) {
            errors.push('Threshold must be a positive number');
        }
        
        // Type-specific validations
        if (rule.type === 'defect_rate' && rule.threshold > 100) {
            errors.push('Defect rate threshold cannot exceed 100%');
        }
        
        if (rule.type === 'low_confidence' && (rule.threshold < 0 || rule.threshold > 100)) {
            errors.push('Confidence threshold must be between 0 and 100');
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    }

    // ==================== DEFECT ANALYSIS ENDPOINTS (MOCK) ====================
    
    async getDefectAnalysis(filters = {}, options = {}) {
        console.warn('[API] getDefectAnalysis endpoint not implemented - returning mock data');
        return Promise.resolve({ 
            total_defects: 0, 
            avg_defect_size: 0, 
            spatial_clusters: [], 
            hotspot_regions: [],
            defect_type_spatial_patterns: {}
        });
    }

    async getDefectTypes(filters = {}, options = {}) {
        console.warn('[API] getDefectTypes endpoint not implemented - returning mock data based on filters:', filters);
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/defect-types?${params.toString()}`;
        return Promise.resolve({ 
            types: [], 
            total_defect_instances: 0 
        });
    }

    // ==================== ADVANCED ANALYTICS (MOCK) ====================
    
    async getRootCauseAnalysis(filters = {}, options = {}) {
        console.warn('[API] getRootCauseAnalysis endpoint not implemented - returning mock data');
        return Promise.resolve({ 
            temporal_patterns: {}, 
            station_patterns: {}, 
            lot_patterns: {},
            environmental_factors: {},
            model_performance: {},
            insights: [], 
            recommendations: [] 
        });
    }

    async getPredictiveInsights(filters = {}, options = {}) {
        console.warn('[API] getPredictiveInsights endpoint not implemented - returning mock data');
        return Promise.resolve({ 
            defect_rate_forecast: {}, 
            failure_risk_assessment: {}, 
            maintenance_recommendations: {},
            quality_trends: {},
            anomaly_detection: {}
        });
    }

    async getModelPerformance(filters = {}, options = {}) {
        console.warn('[API] getModelPerformance endpoint not implemented - returning mock data');
        return Promise.resolve({ 
            accuracy_metrics: {}, 
            agreement_analysis: {}, 
            confidence_distribution: {},
            error_analysis: {},
            bias_analysis: {},
            overall_score: 0,
            recommendations: []
        });
    }

    // ==================== HEATMAP AGGREGATION ENDPOINTS ====================

    /**
     * Get inspection data that contains heatmap information for aggregation
     * @param {Object} filters - Filter criteria for inspections
     * @returns {Promise<Object>} - Inspection data with heatmap information
     */
    async getHeatmapAggregationData(filters = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/heatmap-aggregation/data?${params.toString()}`;
        console.log('[API] Getting heatmap aggregation data:', url);
        return this.request(url, { method: 'GET' });
    }

    /**
     * Generate an aggregated heatmap from multiple inspections with optimizations
     * @param {string} modelName - Name of the model to aggregate heatmaps for
     * @param {string} aggregationMethod - Method to use (average, max, min, median)
     * @param {Object} options - Additional options
     * @param {number} options.thresholdPercentile - Percentile for hotspot analysis (50-99)
     * @param {boolean} options.useCache - Whether to use cached results
     * @param {Object} filters - Filter criteria for inspections
     * @returns {Promise<Object>} - Aggregated heatmap result
     */
    async generateAggregatedHeatmap(modelName, aggregationMethod = 'average', options = {}, filters = {}) {
        const { 
            thresholdPercentile = 80.0, 
            useCache = true 
        } = options;

        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/heatmap-aggregation/generate?${params.toString()}`;
        
        console.log('[API] Generating aggregated heatmap:', { 
            modelName, 
            aggregationMethod, 
            thresholdPercentile, 
            useCache, 
            filters 
        });
        
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                model_name: modelName,
                aggregation_method: aggregationMethod,
                threshold_percentile: thresholdPercentile,
                use_cache: useCache
            })
        });
    }

    /**
     * OPTIMIZED: Generate heatmap with streaming progress updates
     * @param {string} modelName - Name of the model to aggregate heatmaps for
     * @param {string} aggregationMethod - Method to use (average, max, min, median)
     * @param {Object} options - Additional options
     * @param {Object} filters - Filter criteria for inspections
     * @param {Function} onProgress - Callback for progress updates
     * @returns {Promise<Object>} - Final aggregated heatmap result
     */
    async generateAggregatedHeatmapWithProgress(modelName, aggregationMethod = 'average', options = {}, filters = {}, onProgress = null) {
        const { 
            thresholdPercentile = 80.0, 
            useCache = true 
        } = options;

        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/heatmap-aggregation/generate-stream?${params.toString()}`;
        
        console.log('[API] Starting streaming heatmap generation:', { 
            modelName, 
            aggregationMethod, 
            thresholdPercentile, 
            useCache 
        });

        // FIXED: Use window.fetch (which is authenticatedFetch) instead of manual auth
        // authenticatedFetch handles Authorization headers, token refresh, and credentials
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
                model_name: modelName,
                aggregation_method: aggregationMethod,
                threshold_percentile: thresholdPercentile,
                use_cache: useCache
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let finalResult = null;

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const lines = decoder.decode(value).split('\n');
                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const progressData = JSON.parse(line);
                            
                            if (onProgress) {
                                onProgress(progressData);
                            }

                            if (progressData.stage === 'complete' && progressData.result) {
                                finalResult = progressData.result;
                            } else if (progressData.stage === 'error') {
                                throw new Error(progressData.message);
                            }
                        } catch (parseError) {
                            console.warn('[API] Failed to parse progress data:', parseError);
                        }
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }

        if (!finalResult) {
            throw new Error('Stream ended without final result');
        }

        return finalResult;
    }

    /**
     * Get list of available models that have heatmap data
     * @param {Object} filters - Filter criteria for inspections
     * @returns {Promise<Array>} - Array of model names
     */
    async getAvailableHeatmapModels(filters = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/heatmap-aggregation/models?${params.toString()}`;
        console.log('[API] Getting available heatmap models:', url);
        return this.request(url, { method: 'GET' });
    }

    /**
     * OPTIMIZED: Clear specific heatmap cache entry (admin only)
     * @param {string} cacheKey - Cache key to clear
     * @returns {Promise<Object>} - Success response
     */
    async clearHeatmapCache(cacheKey) {
        const url = `${this.baseUrl}/reports/heatmap-aggregation/cache/${cacheKey}`;
        console.log('[API] Clearing heatmap cache:', cacheKey);
        return this.request(url, { method: 'DELETE' });
    }

    /**
     * OPTIMIZED: Get heatmap cache statistics (admin only)
     * @returns {Promise<Object>} - Cache statistics
     */
    async getHeatmapCacheStats() {
        const url = `${this.baseUrl}/reports/heatmap-aggregation/cache/stats`;
        console.log('[API] Getting heatmap cache statistics');
        return this.request(url, { method: 'GET' });
    }

    /**
     * Download aggregated heatmap as an image file by proxying through the backend.
     * @param {string} cacheKey - The cache key of the generated heatmap.
     * @param {string} filename - Desired filename for the download.
     */
    async downloadHeatmapImage(cacheKey, filename = 'aggregated_heatmap.png') {
        if (!cacheKey) {
            const error = new Error("A valid cacheKey is required to download the heatmap.");
            console.error('[API] Error downloading heatmap image:', error);
            throw error;
        }

        try {
            console.log(`[API] Downloading heatmap image via backend for cache key: ${cacheKey}`);
            
            const downloadUrl = `${this.baseUrl}/reports/heatmap-aggregation/download/${cacheKey}`;

            // FIXED: Use window.fetch (authenticatedFetch) instead of manual token
            const response = await fetch(downloadUrl);

            if (!response.ok) {
                throw new Error(`Failed to download image from backend: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary link to trigger the browser's download functionality.
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            window.URL.revokeObjectURL(url);
            
            console.log('[API] Successfully initiated download for heatmap image:', filename);
        } catch (error) {
            console.error('[API] Error downloading heatmap image via backend:', error);
            throw error;
        }
    }

    /**
     * OPTIMIZED: Validate heatmap aggregation parameters
     * @param {string} modelName - Model name to validate
     * @param {string} aggregationMethod - Aggregation method to validate
     * @param {Object} options - Additional options to validate
     * @returns {Object} - Validation result
     */
    validateHeatmapAggregationParams(modelName, aggregationMethod, options = {}) {
        const validMethods = ['average', 'max', 'min', 'median'];
        const errors = [];
        
        if (!modelName || typeof modelName !== 'string') {
            errors.push('Model name is required and must be a string');
        }
        
        if (!validMethods.includes(aggregationMethod)) {
            errors.push(`Aggregation method must be one of: ${validMethods.join(', ')}`);
        }
        
        const { thresholdPercentile, useCache } = options;
        
        if (thresholdPercentile !== undefined) {
            if (typeof thresholdPercentile !== 'number' || thresholdPercentile < 50 || thresholdPercentile > 99) {
                errors.push('Threshold percentile must be a number between 50 and 99');
            }
        }
        
        if (useCache !== undefined && typeof useCache !== 'boolean') {
            errors.push('useCache must be a boolean value');
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }

    // ==================== HEATMAP UTILITY METHODS ====================

    /**
     * Process heatmap visualization data from inspection results
     * @param {Object} inspection - Inspection object
     * @returns {Object} - Processed heatmap information
     */
    processHeatmapData(inspection) {
        const heatmapInfo = {
            available_heatmaps: {},
            has_heatmaps: false
        };
        
        if (!inspection) return heatmapInfo;
        
        // Check model_results structure (new format)
        const modelResults = inspection.model_results;
        if (modelResults) {
            const modelsData = modelResults.models || modelResults;
            
            if (typeof modelsData === 'object') {
                Object.entries(modelsData).forEach(([modelName, modelData]) => {
                    if (typeof modelData === 'object' && modelData !== null) {
                        const heatmapFields = [
                            'heatmap_url', 'heatmap_data', 'visualization', 
                            'overlay_url', 'segmentation_url', 'annotation_url'
                        ];
                        
                        for (const field of heatmapFields) {
                            if (modelData[field] && typeof modelData[field] === 'string') {
                                heatmapInfo.available_heatmaps[modelName] = {
                                    url: modelData[field],
                                    confidence: modelData.confidence || 0,
                                    classification: modelData.classification || 'unknown',
                                    type: field
                                };
                                break;
                            }
                        }
                    }
                });
            }
        }
        
        // Check legacy structures
        const legacyFields = [
            { field: 'unsupervised_result', model: 'unsupervised' },
            { field: 'localization_result', model: 'localization' }
        ];
        
        legacyFields.forEach(({ field, model }) => {
            const resultData = inspection[field];
            if (resultData && typeof resultData === 'object') {
                const heatmapUrl = resultData.heatmap_url || resultData.permanent_url;
                if (heatmapUrl) {
                    heatmapInfo.available_heatmaps[model] = {
                        url: heatmapUrl,
                        confidence: resultData.confidence || 0,
                        classification: resultData.classification || 'unknown',
                        type: 'legacy'
                    };
                }
            }
        });
        
        heatmapInfo.has_heatmaps = Object.keys(heatmapInfo.available_heatmaps).length > 0;
        return heatmapInfo;
    }

    // ==================== DEFECT ANALYSIS ENDPOINTS ====================

    async getInspectionsByDefectType(filters = {}, page = 1, limit = 50, options = {}) {
        console.log('[API] Getting inspections by defect type with filters:', filters);
        
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                dateRange: filters.dateRange || filters.date_range || 'all',
                sortBy: filters.sortBy || 'inspection_time',
                sortDirection: filters.sortDirection || 'desc'
            });
            
            // Add standard filters
            if (filters.project) params.append('project', filters.project);
            if (filters.station) params.append('station', filters.station);
            if (filters.lotId || filters.lot_id) params.append('lot_id', filters.lotId || filters.lot_id);
            if (filters.session_id) params.append('session_id', filters.session_id);
            if (filters.result) params.append('result', filters.result);
            if (filters.modelAgreement) params.append('model_agreement', filters.modelAgreement);
            if (filters.confidence && filters.confidence > 0) {
                params.append('confidence', filters.confidence.toString());
            }
            
            // Add the defect type filter
            if (filters.defectType) params.append('defect_type', filters.defectType);
            
            const url = `${this.baseUrl}/reports/inspections?${params}`;
            console.log('[API] Requesting inspections by defect type from:', url);
            
            const response = await this.request(url, { method: 'GET', ...options });
            
            // The API returns paginated data, but we want just the array for this use case
            return response?.data || [];
            
        } catch (error) {
            console.error('[API] Error getting inspections by defect type:', error);
            throw error;
        }
    }

    /**
     * NEW: Fetches pre-aggregated defect analysis data from the backend.
     * @param {Object} filters - Filter criteria for inspections
     * @returns {Promise<Object>} - Aggregated defect analysis data
     */
    async getDefectTypeAnalysis(filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/defect-type-analysis?${params.toString()}`;
        console.log('[API] Getting defect type analysis from backend:', url);
        return this.request(url, { method: 'GET', ...options });
    }

    /**
     * OPTIMIZED: Get statistics about heatmap availability with memory efficiency
     * @param {Array} inspections - Array of inspection objects
     * @returns {Object} - Statistics about heatmap availability
     */
    getHeatmapStatistics(inspections) {
        if (!Array.isArray(inspections)) return null;
        
        const stats = {
            total_inspections: inspections.length,
            inspections_with_heatmaps: 0,
            model_distribution: {},
            coverage_percentage: 0,
            memory_estimate_mb: 0
        };
        
        // Estimate memory usage for processing
        const avgImageSize = 256 * 256 * 4; // bytes (256x256 RGBA)
        
        inspections.forEach(inspection => {
            const heatmapInfo = this.processHeatmapData(inspection);
            
            if (heatmapInfo.has_heatmaps) {
                stats.inspections_with_heatmaps++;
                
                Object.keys(heatmapInfo.available_heatmaps).forEach(modelName => {
                    stats.model_distribution[modelName] = (stats.model_distribution[modelName] || 0) + 1;
                });
            }
        });
        
        stats.coverage_percentage = stats.total_inspections > 0 
            ? (stats.inspections_with_heatmaps / stats.total_inspections) * 100 
            : 0;
        
        // Estimate memory usage for the largest model
        const maxModelCount = Math.max(...Object.values(stats.model_distribution), 0);
        stats.memory_estimate_mb = (maxModelCount * avgImageSize) / (1024 * 1024);
        
        return stats;
    }

    // OPTIMIZED: Add performance monitoring
    /**
     * Monitor heatmap generation performance
     * @param {Function} operation - Async operation to monitor
     * @param {string} operationName - Name for logging
     * @returns {Promise<any>} - Operation result
     */
    async monitorHeatmapOperation(operation, operationName) {
        const startTime = performance.now();
        console.log(`[API] Starting ${operationName}...`);
        
        try {
            const result = await operation();
            const duration = performance.now() - startTime;
            console.log(`[API] ${operationName} completed in ${duration.toFixed(2)}ms`);
            return result;
        } catch (error) {
            const duration = performance.now() - startTime;
            console.error(`[API] ${operationName} failed after ${duration.toFixed(2)}ms:`, error);
            throw error;
        }
    }
    
    // ==================== ENHANCED INSPECTION ENDPOINTS ====================
    
    // FIX: THIS IS THE CORRECTED FUNCTION
    async getInspections(filters = {}, page = 1, limit = 20, options = {}) {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            dateRange: filters.dateRange || 'week',
            sortBy: filters.sortBy || 'timestamp', // The backend uses 'sortBy' not 'sort_by'
            sortDirection: filters.sortDirection || 'desc'
        });
        
        if (filters.project) params.append('project', filters.project);
        if (filters.station) params.append('station', filters.station);
        if (filters.lotId) params.append('lot_id', filters.lotId);
        if (filters.session_id) params.append('session_id', filters.session_id);
        if (filters.result) params.append('result', filters.result);
        if (filters.defectType) params.append('defect_type', filters.defectType);
        if (filters.modelAgreement) params.append('model_agreement', filters.modelAgreement);
        if (filters.confidence && filters.confidence > 0) {
            params.append('confidence', filters.confidence.toString());
        }

        // Add the missing includeManualImages parameter
        if (typeof filters.includeManualImages === 'boolean') {
            params.append('includeManualImages', filters.includeManualImages);
        }
        
        const url = `${this.baseUrl}/reports/inspections?${params}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getInspectionDetails(inspectionId, options = {}) {
        const url = `${this.baseUrl}/reports/inspections/${inspectionId}`;
        return this.request(url, { method: 'GET', ...options });
    }

    // ==================== CLASSIFICATION CONFIRMATION ====================
    
    async confirmClassification(inspectionId, status) {
        const url = `${this.baseUrl}/reports/confirm_classification`;
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                report_id: inspectionId,
                status: status
            })
        });
    }

    // ==================== STATION ENDPOINTS ====================
    
    async getStations(filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/station/list?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getStationTrend(stationId, filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/trend?level=station&identifier=${stationId}&${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    // ==================== PROJECT ENDPOINTS ====================

    /**
     * NEW: Fetches a paginated list of basic projects.
     * @param {number} page - Current page number.
     * @param {number} limit - Items per page.
     * @param {Object} options - Fetch options.
     * @returns {Promise<Object>} - Paginated response object ({ data: [], total: ... })
     */
    async getPaginatedProjects(page = 1, limit = 100, options = {}) {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString()
        });
        const url = `${this.baseUrl}/projects?${params.toString()}`;
        console.log(`[API] Getting paginated projects from: ${url}`);
        // This will return the object (e.g., { data: [...], total: ... })
        return this.request(url, { method: 'GET', ...options });
    }
    
    async getProjectSummaries(filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/project-summaries?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getEnhancedProjectDetails(projectName, filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/projects/${encodeURIComponent(projectName)}/details?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }    

    async getProjectTrend(projectName, filters = {}, options = {}) {
        const params = new URLSearchParams(this.cleanFilters(filters));
        const url = `${this.baseUrl}/reports/trend?level=project&identifier=${encodeURIComponent(projectName)}&${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }
        
    async getProjects(options = {}) {
        const url = `${this.baseUrl}/projects`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getLotIdentifiers(projectName, options = {}) {
        if (!projectName) {
            return [];
        }
        const url = `${this.baseUrl}/general/lot-identifiers?project_name=${encodeURIComponent(projectName)}`;
        return this.request(url, { method: 'GET', ...options });
    }

    /**
     * Updated to accept and include filters in the POST body for project details lookup.
     * @param {Array<string>} projectNames - List of project names to get details for.
     * @param {Object} filters - Filters to apply to the data aggregation (e.g., dateRange, includeManualImages).
     * @param {Object} options - Fetch options (e.g., bypassCache).
     * @returns {Promise<Object>} - Map of project details.
     */
    async getProjectDetails(projectNames, filters = {}, options = {}) {
        const url = `${this.baseUrl}/reports/projects/details`;
        
        // Construct the POST body, merging project names, trend request, and cleaned filters
        const payload = { 
            project_names: projectNames, 
            include_trends: true,
            ...this.cleanFilters(filters)
        };

        return this.request(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            ...options
        });
    }

    // ==================== LOT ENDPOINTS ====================
    
    async getLots(filters = {}, page = 1, limit = 20, options = {}) {
        const cleanedFilters = this.cleanFilters(filters);
        const params = new URLSearchParams(cleanedFilters);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        const url = `${this.baseUrl}/reports/lots?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getLotTrend(lotId, dateRange = 'month', options = {}) {
        const url = `${this.baseUrl}/reports/trend?level=lot&identifier=${lotId}&date_range=${dateRange}`;
        return this.request(url, { method: 'GET', ...options });
    }

    // ==================== SESSION ENDPOINTS ====================

    async getSessions(filters = {}, page = 1, limit = 10, options = {}) {
        const cleanedFilters = this.cleanFilters(filters);
        const params = new URLSearchParams(cleanedFilters);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        const url = `${this.baseUrl}/reports/sessions?${params.toString()}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getSessionTrend(sessionId, dateRange = 'week', options = {}) {
        const url = `${this.baseUrl}/reports/trend?level=session&identifier=${sessionId}&date_range=${dateRange}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getSessionInspections(sessionId, page = 1, limit = 50, options = {}) {
        if (!sessionId) {
            console.warn('[API] getSessionInspections called with undefined sessionId');
            return Promise.resolve({ data: [], total: 0, page: 1, total_pages: 1 });
        }
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            session_id: sessionId,
            dateRange: 'all',
            sortBy: 'inspection_time',
            sortDirection: 'desc'
        });
        const url = `${this.baseUrl}/reports/inspections?${params}`;
        return this.request(url, { method: 'GET', ...options });
    }

    async getAllSessionInspections(sessionId, options = {}) {
        if (!sessionId) {
            console.warn('[API] getAllSessionInspections called with undefined sessionId');
            return [];
        }
        const allInspections = [];
        let currentPage = 1;
        let totalPages = 1;
        const batchSize = 50;
        try {
            do {
                const response = await this.getSessionInspections(sessionId, currentPage, batchSize, options);
                if (response?.data) {
                    allInspections.push(...response.data);
                    totalPages = response.total_pages || 1;
                } else {
                    break;
                }
                currentPage++;
                if (currentPage > 50 || allInspections.length > 2500) {
                    console.warn(`[API] Stopping session inspection fetch at safety limit: page ${currentPage}, total ${allInspections.length}`);
                    break;
                }
            } while (currentPage <= totalPages);
            return allInspections;
        } catch (error) {
            console.error(`[API] Error fetching all session inspections:`, error);
            throw error;
        }
    }

    // ==================== MODEL VISUALIZATION UTILITIES ====================
    
    getModelVisualizations(inspection) {
        const visualizations = [];
        if (!inspection) return visualizations;

        if (inspection.unsupervised_result?.heatmap_url) {
            visualizations.push({ type: 'heatmap', model: 'unsupervised', url: inspection.unsupervised_result.heatmap_url, label: 'Anomaly Heatmap (Legacy)' });
        }
        if (inspection.localization_result?.permanent_url) {
            visualizations.push({ type: 'localization', model: 'localization', url: inspection.localization_result.permanent_url, label: 'Defect Localization (Legacy)' });
        }

        const modelResults = inspection.model_results;
        if (modelResults) {
            const modelsData = modelResults.models || modelResults;
            if (typeof modelsData === 'object') {
                Object.entries(modelsData).forEach(([modelName, modelData]) => {
                    if (typeof modelData === 'object' && modelData !== null) {
                        const vizFields = [
                            { key: 'heatmap_url', type: 'heatmap', label: 'Heatmap' },
                            { key: 'localization_image_url', type: 'localization', label: 'Localization' },
                            { key: 'visualization', type: 'visualization', label: 'Visualization' },
                            { key: 'heatmap_data', type: 'heatmap', label: 'Heatmap Data' },
                            { key: 'segmentation_url', type: 'segmentation', label: 'Segmentation' },
                            { key: 'overlay_url', type: 'overlay', label: 'Overlay' },
                            { key: 'annotation_url', type: 'annotation', label: 'Annotation' }
                        ];
                        vizFields.forEach(({ key, type, label }) => {
                            if (modelData[key] && typeof modelData[key] === 'string') {
                                visualizations.push({ type, model: modelName, url: modelData[key], label: `${label} (${modelName})`, confidence: modelData.confidence, defectType: modelData.defect_type, numDefects: modelData.num_defects });
                            }
                        });
                    }
                });
            }
        }
        return visualizations;
    }

    hasModelVisualizations(inspection) {
        return this.getModelVisualizations(inspection).length > 0;
    }

    async checkImageAccessibility(imageUrl) {
        if (!imageUrl || typeof imageUrl !== 'string') return false;
        try {
            const response = await fetch(imageUrl, { method: 'HEAD' });
            return response.ok;
        } catch (error) {
            console.warn(`[API] Image accessibility check failed for ${imageUrl}:`, error.message);
            return false;
        }
    }

    async downloadImage(imageUrl, filename) {
        try {
            const response = await fetch(imageUrl);
            if (!response.ok) throw new Error(`Failed to download image: ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`[API] Error downloading image ${filename}:`, error);
            throw error;
        }
    }

    // ==================== EXPORT ENDPOINTS ====================
    
    async exportReport(reportType, format, filters = {}) {
        const url = `${this.baseUrl}/reports/export`;
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                report_type: reportType,
                format: format,
                filters: this.cleanFilters(filters)
            })
        });
    }

    async exportInspectionData(format = 'csv', filters = {}) {
        try {
            const params = new URLSearchParams({ format, ...this.cleanFilters(filters) });
            
            // FIXED: Use window.fetch (authenticatedFetch) instead of manual Authorization header
            const response = await fetch(`${this.baseUrl}/reports/export/inspections?${params}`, {
                method: 'GET'
                // No need for headers - authenticatedFetch adds them automatically
            });
            
            if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `inspection_report_${Date.now()}.${format}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(`[API] Error exporting inspection data as ${format}:`, error);
            throw error;
        }
    }

    // ==================== UTILITY METHODS ====================
    
    cleanFilters(filters) {

        const cleaned = { ...filters };
        // allow includeManualImages to reach backend
        if (typeof cleaned.includeManualImages === 'boolean') {
            cleaned.includeManualImages = cleaned.includeManualImages ? 'true' : 'false';
        }
        // page/limit passthrough (some endpoints already support it)
        if (cleaned.limit) cleaned.limit = String(cleaned.limit);
        if (cleaned.page) cleaned.page = String(cleaned.page);


        // const cleaned = {};
        const filterMap = {
            lotId: 'lot_id',
            defectType: 'defect_type',
            modelAgreement: 'model_agreement',
            dateRange: 'date_range'
        };
        for (const key in filters) {
            if (Object.prototype.hasOwnProperty.call(filters, key)) {
                const value = filters[key];
                if (value !== null && value !== undefined && value !== '') {
                    const backendKey = filterMap[key] || key;
                    cleaned[backendKey] = value;
                }
            }
        }
        return cleaned;
    }

    clearCache() {
        this.cache.clear();
        console.log('[API] Cache cleared');
    }

    getStats() {
        return {
            requestCount: this.requestCount,
            cacheSize: this.cache.size,
            cacheKeys: Array.from(this.cache.keys())
        };
    }

    // ==================== VALIDATION UTILITIES ====================
    
    isValidInspectionId(id) {
        return typeof id === 'string' && id.length === 24 && /^[0-9a-fA-F]{24}$/.test(id);
    }

    isValidClassificationStatus(status) {
        return ['normal', 'defective', 'unsure'].includes(status);
    }

    isValidDateRange(dateRange) {
        const validRanges = ['today', 'week', 'month', 'year', 'all'];
        return validRanges.includes(dateRange);
    }

    // ==================== ENHANCED PROJECTS ENDPOINTS ====================

    /**
     * Get complete project data by calling the main projects endpoint
     * This should have more complete information than project-summaries
     */
    async getProjectsComplete(options = {}) {
        console.log('[API] Getting complete projects data from main endpoint...');
        
        try {
            // This calls GET /projects which should have model_trained, latest_image, etc.
            const url = `${this.baseUrl}/projects`;
            const response = await this.request(url, { method: 'GET', ...options });
            
            console.log('[API] Complete projects response:', response);
            
            // The response should be a paginated response with .data property
            if (response && Array.isArray(response.data)) {
                return response.data;
            } else if (Array.isArray(response)) {
                // Some endpoints return array directly
                return response;
            } else {
                console.warn('[API] Unexpected projects response format:', response);
                return [];
            }
        } catch (error) {
            console.error('[API] Error getting complete projects:', error);
            throw error;
        }
    }

    /**
     * Get enhanced project data by combining multiple sources
     */
    async getEnhancedProjectSummaries(filters = {}, options = {}) {
        console.log('[API] Getting enhanced project summaries...');
        
        try {
            // Get data from both endpoints
            const [summaries, projects] = await Promise.allSettled([
                this.getProjectSummaries(filters, options),
                this.getProjectsComplete(options)
            ]);
            
            let finalData = [];
            
            // Start with summaries data (has statistics)
            if (summaries.status === 'fulfilled' && summaries.value) {
                finalData = Array.isArray(summaries.value) ? summaries.value : [];
            }
            
            // Enrich with complete projects data
            if (projects.status === 'fulfilled' && projects.value) {
                const projectsMap = new Map();
                
                // Create lookup map from complete projects data
                projects.value.forEach(project => {
                    const projectName = project.project_name || project.name;
                    if (projectName) {
                        projectsMap.set(projectName, project);
                    }
                });
                
                // Enrich summaries with complete project data
                finalData = finalData.map(summary => {
                    const enhanced = { ...summary };
                    const completeProject = projectsMap.get(summary.project_name);
                    
                    if (completeProject) {
                        // Merge additional fields from complete project data
                        enhanced.model_trained = completeProject.model_trained;
                        enhanced.latest_image = completeProject.latest_image;
                        enhanced.preview_image = completeProject.preview_image;
                        enhanced.training_images = completeProject.training_images;
                        enhanced.total_sessions = completeProject.total_sessions;
                        enhanced.active_sessions = completeProject.active_sessions;
                        enhanced.created_at = completeProject.created_at;
                        enhanced.last_modified = completeProject.last_modified;
                        
                        // Use complete project description if summary doesn't have one
                        if (!enhanced.description && completeProject.description) {
                            enhanced.description = completeProject.description;
                        }
                        
                        console.log(`[API] Enhanced ${summary.project_name} with complete project data`);
                    }
                    
                    return enhanced;
                });
                
                // Add any projects that exist in complete data but not in summaries
                projects.value.forEach(project => {
                    const projectName = project.project_name || project.name;
                    const existsInSummaries = finalData.some(s => s.project_name === projectName);
                    
                    if (!existsInSummaries && projectName) {
                        // Create a summary-like object from complete project data
                        const syntheticSummary = {
                            project_name: projectName,
                            description: project.description || '',
                            status: project.status || 'unknown',
                            trained: project.model_trained || false,
                            model_trained: project.model_trained,
                            latest_image: project.latest_image,
                            training_images: project.training_images,
                            statistics: project.statistics || {
                                total_inspections: project.total_inspections || 0,
                                defect_count: project.defect_count || 0
                            },
                            last_inspection: project.last_modified,
                            created_at: project.created_at,
                            last_modified: project.last_modified
                        };
                        
                        finalData.push(syntheticSummary);
                        console.log(`[API] Added missing project from complete data: ${projectName}`);
                    }
                });
            }
            
            console.log('[API] Final enhanced project data:', finalData);
            return finalData;
            
        } catch (error) {
            console.error('[API] Error getting enhanced project summaries:', error);
            throw error;
        }
    }

    /**
     * Try to get a project image with multiple fallback strategies
     */
    async getProjectImageWithFallbacks(projectName) {
        console.log(`[API] Getting project image for: ${projectName}`);
        
        const possibleUrls = [
            `${this.baseUrl}/projects/${encodeURIComponent(projectName)}/image`,
            `${this.baseUrl}/projects/${encodeURIComponent(projectName)}/preview`,
            `http://localhost:5000/projects/${projectName}/preview.jpg`,
            `http://localhost:5000/projects/${projectName}/latest.jpg`,
            `http://localhost:5000/assets/projects/${projectName}.jpg`
        ];
        
        for (const url of possibleUrls) {
            try {
                // FIXED: Use window.fetch (authenticatedFetch) - no need for manual Authorization
                const response = await fetch(url, { 
                    method: 'HEAD'
                    // No headers needed - authenticatedFetch adds Authorization automatically
                });
                
                if (response.ok) {
                    console.log(`[API] Found accessible image for ${projectName}: ${url}`);
                    return url;
                }
            } catch (error) {
                // Continue to next URL
            }
        }
        
        console.log(`[API] No accessible image found for ${projectName}, using default`);
        return 'http://localhost:5000/assets/no-training-image.png';
    }

    /**
     * Check if a project has a trained model by trying the specific project endpoint
     */
    async checkProjectModelStatus(projectName) {
        console.log(`[API] Checking model status for project: ${projectName}`);
        
        try {
            const url = `${this.baseUrl}/projects/${encodeURIComponent(projectName)}`;
            const response = await this.request(url, { method: 'GET' });
            
            if (response) {
                console.log(`[API] Project details for ${projectName}:`, response);
                
                // Look for model training indicators
                const indicators = {
                    model_trained: response.model_trained,
                    has_models: response.has_models,
                    model_status: response.model_status,
                    training_status: response.training_status,
                    latest_image: response.latest_image,
                    training_images: response.training_images
                };
                
                console.log(`[API] Model indicators for ${projectName}:`, indicators);
                return indicators;
            }
        } catch (error) {
            console.log(`[API] Could not get project details for ${projectName}:`, error.message);
        }
        
        return null;
    }

    /**
     * Get project image with multiple fallback attempts
     */
    async getProjectImage(projectName) {
        const imagePaths = [
            `${this.baseUrl}/projects/${encodeURIComponent(projectName)}/image`,
            `${this.baseUrl}/projects/${encodeURIComponent(projectName)}/preview`,
            `${this.baseUrl}/projects/${encodeURIComponent(projectName)}/latest-image`,
            `http://localhost:5000/projects/${projectName}/preview.jpg`,
            `http://localhost:5000/assets/projects/${projectName}.jpg`
        ];

        for (const imagePath of imagePaths) {
            try {
                // Check if image is accessible
                const response = await fetch(imagePath, { method: 'HEAD' });
                if (response.ok) {
                    console.log(`[API] Found project image for ${projectName}: ${imagePath}`);
                    return imagePath;
                }
            } catch (error) {
                console.log(`[API] Image path failed for ${projectName}: ${imagePath}`);
            }
        }

        console.log(`[API] No accessible image found for ${projectName}, using default`);
        return 'http://localhost:5000/assets/no-training-image.png';
    }

    // ==================== AUTH HELPER METHODS ====================
    
    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        const token = localStorage.getItem('access_token');
        return !!token;
    }

    /**
     * Get current user from token (basic decode)
     */
    getCurrentUser() {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) return null;

            const parts = token.split('.');
            if (parts.length !== 3) return null;

            const payload = JSON.parse(atob(parts[1]));
            return {
                username: payload.sub,
                roles: payload.roles || [],
                user_id: payload.user_id
            };
        } catch (error) {
            console.error('[API] Error decoding token:', error);
            return null;
        }
    }
}

// ==================== CONSTANTS ====================
export const REPORTING_CONSTANTS = {
    MAX_PAGE_SIZE: 100,
    DEFAULT_PAGE_SIZE: 50,
    SUPPORTED_IMAGE_FORMATS: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    VISUALIZATION_TYPES: ['heatmap', 'localization_image', 'visualization', 'segmentation', 'overlay', 'annotation'],
    CLASSIFICATION_STATUSES: ['normal', 'defective', 'unsure'],
    DATE_RANGES: ['today', 'week', 'month', 'year', 'all'],
    SORT_DIRECTIONS: ['asc', 'desc']
};

// Add these constants to the REPORTING_CONSTANTS export
export const HEATMAP_CONSTANTS = {
    AGGREGATION_METHODS: [
        { value: 'average', label: 'Average', description: 'Mean intensity across all images' },
        { value: 'max', label: 'Maximum', description: 'Peak intensity at each pixel' },
        { value: 'min', label: 'Minimum', description: 'Lowest intensity at each pixel' },
        { value: 'median', label: 'Median', description: 'Median intensity (robust to outliers)' }
    ],
    SUPPORTED_FORMATS: ['png', 'jpg', 'jpeg'],
    MAX_INSPECTIONS_FOR_AGGREGATION: 1000,
    DEFAULT_AGGREGATION_METHOD: 'average',
    DEFAULT_THRESHOLD_PERCENTILE: 80,
    MEMORY_WARNING_THRESHOLD_MB: 500,
    RECOMMENDED_BATCH_SIZE: 50,
    CACHE_EXPIRY_HOURS: 24
};

// Create singleton instance
export const api = new ReportingAPI();